import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import ErrorBoundary from '../ErrorBoundary';
import Topbar from './Topbar';
import Sidebar from './Sidebar';
import Footer from './Footer';
import TopMenuNav from './TopMenuNav';
import { getSidebarItems } from './config/SidebarItems';
import ModuleGuard from '../ModuleGuard';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useEffectiveSidebarLayout } from '../../hooks/useEffectiveSidebarLayout';
import { usesSidebar, usesTopMenu } from '../../config/sidebarLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { useModuleEntitlements } from '../../modules/admin/module-management';
import { cn } from '../../lib/utils';
import { getBookmarks, getCachedBookmarks, reconstructBookmarkIcon, BookmarkItem } from '../../utils/bookmarks';
import UserThemeSync from '../../modules/account-settings/UserThemeSync';
import { moduleConfig as accountSettingsConfig } from '../../modules/account-settings/config';
import { moduleConfig as usersConfig } from '../../modules/admin/users/config';
import { moduleConfig as itemUnitsConfig } from '../../modules/supplies/item-units/config';
import { moduleConfig as itemsConfig } from '../../modules/supplies/items/config';
import { moduleConfig as supplyCasesConfig } from '../../modules/supplies/supply-cases/config';
import { moduleConfig as requisitionsConfig } from '../../modules/supplies/requisitions/requisitionsModuleConfig';
import { moduleConfig as supplierEnquiriesConfig } from '../../modules/supplies/supplier-enquiries/supplierEnquiriesModuleConfig';
import { moduleConfig as supplierQuotationsConfig } from '../../modules/supplies/supplier-quotations/supplierQuotationsModuleConfig';
import { moduleConfig as supplyDeliveriesConfig } from '../../modules/supplies/supply-deliveries/supplyDeliveriesModuleConfig';
import { moduleConfig as supplyPurchaseOrdersConfig } from '../../modules/supplies/supply-purchase-orders/supplyPurchaseOrdersModuleConfig';
import { moduleConfig as supplyForwardingStatusesConfig } from '../../modules/supplies/supply-forwarding-statuses/config';
import { moduleConfig as supplyForwardingsConfig } from '../../modules/supplies/supply-forwardings/supplyForwardingsModuleConfig';
import { moduleConfig as supplyInvoicesConfig } from '../../modules/supplies/supply-invoices/supplyInvoicesModuleConfig';
import { moduleConfig as quotationEvaluationsConfig } from '../../modules/supplies/quotation-evaluations/quotationEvaluationsModuleConfig';
import { moduleConfig as spareCasesConfig } from '../../modules/spares/spare-cases/config';
import { moduleConfig as spareRequisitionsConfig } from '../../modules/spares/requisitions/requisitionsModuleConfig';
import { moduleConfig as spareSupplierEnquiriesConfig } from '../../modules/spares/supplier-enquiries/supplierEnquiriesModuleConfig';
import { moduleConfig as spareSupplierQuotationsConfig } from '../../modules/spares/supplier-quotations/supplierQuotationsModuleConfig';
import { moduleConfig as spareDeliveriesConfig } from '../../modules/spares/spare-deliveries/spareDeliveriesModuleConfig';
import { moduleConfig as sparePurchaseOrdersConfig } from '../../modules/spares/spare-purchase-orders/sparePurchaseOrdersModuleConfig';
import { moduleConfig as spareForwardingStatusesConfig } from '../../modules/spares/spare-forwarding-statuses/config';
import { moduleConfig as spareForwardingsConfig } from '../../modules/spares/spare-forwardings/spareForwardingsModuleConfig';
import { moduleConfig as spareInvoicesConfig } from '../../modules/spares/spare-invoices/spareInvoicesModuleConfig';
import { moduleConfig as spareQuotationEvaluationsConfig } from '../../modules/spares/quotation-evaluations/quotationEvaluationsModuleConfig';
import { moduleConfig as spareLibrariesConfig } from '../../modules/spares/spare-libraries/config';
import { moduleConfig as spareItemsConfig } from '../../modules/spares/spare-items/config';
import { moduleConfig as countriesConfig } from '../../modules/parameters/countries/config';
import { moduleConfig as currenciesConfig } from '../../modules/parameters/currencies/config';
import { moduleConfig as departmentsConfig } from '../../modules/parameters/departments/config';
import { moduleConfig as storeCategoriesConfig } from '../../modules/parameters/store-categories/config';
import { moduleConfig as vesselsConfig } from '../../modules/parameters/vessels/config';
import { moduleConfig as portsConfig } from '../../modules/parameters/ports/config';
import { moduleConfig as entitiesConfig } from '../../modules/entities/config';
import { moduleConfig as crewConfig } from '../../modules/crew/config';
import {
  buildCrewLookupModuleConfig,
  CREW_LOOKUP_DEFS,
  crewLookupListPath,
} from '../../modules/parameters/crew-lookups';
import { moduleConfig as accDocumentTypesConfig } from '../../modules/accounting/document-types/config';
import { moduleConfig as accAccountTypesConfig } from '../../modules/accounting/account-types/config';
import { moduleConfig as accOperationalTypesConfig } from '../../modules/accounting/operational-types/config';
import { moduleConfig as accCompaniesConfig } from '../../modules/accounting/companies/config';
import { moduleConfig as accAccountsConfig } from '../../modules/accounting/accounts/config';
import { moduleConfig as accPeriodsConfig } from '../../modules/accounting/periods/config';
import { moduleConfig as accCurrencyRatesConfig } from '../../modules/accounting/currency-rates/config';
import { moduleConfig as accDocumentsConfig } from '../../modules/accounting/documents/config';
import { moduleConfig as storeCatalogsConfig } from '../../modules/supplies/store-catalogs/config';
import { ACCOUNTING_REPORT_CATALOG } from '../../modules/accounting/reports/reportCatalog';

const accountingReportRoutes = Object.fromEntries(
  ACCOUNTING_REPORT_CATALOG.flatMap((group) =>
    group.reports.map((report) => [report.path, { module: { title: report.name } }])
  )
);

// Route to module config mapping (template: extend as you add modules)
const routeToConfigMap: Record<string, any> = {
  '/': { module: { title: 'Dashboard' } },
  '/dashboard': { module: { title: 'Dashboard' } },
  '/account-settings': accountSettingsConfig,
  '/parameters/countries': countriesConfig,
  '/parameters/currencies': currenciesConfig,
  '/parameters/departments': departmentsConfig,
  '/parameters/store-categories': storeCategoriesConfig,
  '/parameters/ports': portsConfig,
  '/parameters/vessels': vesselsConfig,
  '/entities': entitiesConfig,
  '/crew': crewConfig,
  '/wrh/sheet': { module: { title: 'WRH Month Sheet' } },
  '/wrh/sheet/rest-record': { module: { title: 'Record of Hours of Rest' } },
  '/wrh/arrangements': { module: { title: 'Crew Working Arrangements' } },
  '/wrh/rules': { module: { title: 'WRH Rules' } },
  '/wrh/rules/report': { module: { title: 'WRH Rules Report' } },
  '/wrh/operation-types': { module: { title: 'Vessel Operation Types' } },
  '/wrh/operation-types/new': { module: { title: 'New operation type' } },
  '/wrh/operation-types/:id': { module: { title: 'Operation type' } },
  '/wrh/operation-types/:id/edit': { module: { title: 'Edit operation type' } },
  '/wrh/compliance': { module: { title: 'WRH Compliance' } },
  '/wrh/compliance/report': { module: { title: 'WRH Compliance Report' } },
  '/wrh/reports': { module: { title: 'WRH Weekly Report' } },
  '/wrh/reports/report': { module: { title: 'WRH Weekly Summary Report' } },
  '/wrh': { module: { title: 'Working Rest Hours' } },
  '/vessel-operations': { module: { title: 'Vessel Operations Schedule' } },
  '/vessel-operations/report': { module: { title: 'Vessel Operations Report' } },
  ...Object.fromEntries(
    CREW_LOOKUP_DEFS.map((def) => [crewLookupListPath(def.kind), buildCrewLookupModuleConfig(def)]),
  ),
  '/accounting/journal-types': accDocumentTypesConfig,
  '/accounting/account-types': accAccountTypesConfig,
  '/accounting/operational-types': accOperationalTypesConfig,
  '/accounting/companies': accCompaniesConfig,
  '/accounting/coa': accAccountsConfig,
  '/accounting/statement-lines': { module: { title: 'Statement lines' } },
  '/accounting/periods': accPeriodsConfig,
  '/accounting/currency-rates': accCurrencyRatesConfig,
  '/accounting/journals': accDocumentsConfig,
  '/accounting/payments/new': { module: { title: 'New payment' } },
  '/accounting/payments': { module: { title: 'Payment' } },
  '/accounting/reports': { module: { title: 'Accounting reports' } },
  '/accounting/reports/analytical-ledgers': { module: { title: 'Analytical ledgers' } },
  '/accounting/reports/journal': { module: { title: 'Journal' } },
  '/accounting/reports/ledger-kartela': { module: { title: 'Ledger details' } },
  '/accounting/reports/trial-balance': { module: { title: 'Trial balance' } },
  '/accounting/reports/aging': { module: { title: 'Aging' } },
  '/supplies/requisitions': requisitionsConfig,
  '/supplies/supplier-enquiries': supplierEnquiriesConfig,
  '/supplies/supplier-quotations': supplierQuotationsConfig,
  '/supplies/supply-deliveries': supplyDeliveriesConfig,
  '/supplies/supply-purchase-orders': supplyPurchaseOrdersConfig,
  '/supplies/supply-forwarding-statuses': supplyForwardingStatusesConfig,
  '/supplies/supply-forwardings': supplyForwardingsConfig,
  '/supplies/supply-invoices': supplyInvoicesConfig,
  '/supplies/quotation-evaluations': quotationEvaluationsConfig,
  '/supplies/supply-cases': supplyCasesConfig,
  '/supplies/port-calls': {
    module: { title: 'Port calls' },
    detail: { subtitle: 'Cases by vessel → port → date' },
  },
  '/supplies/items': itemsConfig,
  '/supplies/item-units': itemUnitsConfig,
  '/supplies/store-catalogs': storeCatalogsConfig,
  '/supplies/excel-document-templates': { module: { title: 'Excel document templates' } },
  '/supplies/reference-templates': { module: { title: 'Reference templates' } },
  '/spares/requisitions': spareRequisitionsConfig,
  '/spares/supplier-enquiries': spareSupplierEnquiriesConfig,
  '/spares/supplier-quotations': spareSupplierQuotationsConfig,
  '/spares/spare-deliveries': spareDeliveriesConfig,
  '/spares/spare-purchase-orders': sparePurchaseOrdersConfig,
  '/spares/spare-forwarding-statuses': spareForwardingStatusesConfig,
  '/spares/spare-forwardings': spareForwardingsConfig,
  '/spares/spare-invoices': spareInvoicesConfig,
  '/spares/quotation-evaluations': spareQuotationEvaluationsConfig,
  '/spares/spare-cases': spareCasesConfig,
  '/spares/port-calls': {
    module: { title: 'Port calls' },
    detail: { subtitle: 'Cases by vessel → port → date' },
  },
  '/spares/spare-items': spareItemsConfig,
  '/spares/spare-libraries': spareLibrariesConfig,
  '/spares/reference-templates': { module: { title: 'Reference templates' } },
  '/admin/users': usersConfig,
  '/admin/import-actions/impa-code': { module: { title: 'IMPA Code import' } },
  '/admin/import-actions/accounts-plan': { module: { title: 'Chart of accounts import' } },
  '/admin/import-actions/companies': { module: { title: 'Companies import' } },
  '/admin/import-actions/journals': { module: { title: 'Journals import' } },
  '/admin/database-browser': { module: { title: 'Database browser' } },
  ...accountingReportRoutes,
};

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const isCompact = false;
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);
  const { appConfig } = useSettings();
  const sidebarStyle = useEffectiveSidebarLayout();
  const showSidebar = usesSidebar(sidebarStyle);
  const showTopMenu = usesTopMenu(sidebarStyle);
  const { user, can, permissionsLoaded } = useAuth();
  const vesselAccountId = useAccountVesselScope();
  const { entitlements } = useModuleEntitlements();
  const canView = React.useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded, user?.role]
  );
  const sidebarItems = React.useMemo(
    () => getSidebarItems(canView, vesselAccountId, entitlements),
    [canView, vesselAccountId, entitlements]
  );
  const topMenuSidebarItems = React.useMemo(
    () => sidebarItems.filter((item) => item.name !== 'Bookmarks'),
    [sidebarItems]
  );

  // Bookmarks for top menu layout (first group). groupKey used for separators between modules.
  type BookmarkEntry = { label: string; path: string; icon?: React.ReactNode; groupKey: string };

  const [bookmarkEntries, setbookmarkEntries] = React.useState<BookmarkEntry[]>(() => {
    const cached = getCachedBookmarks();
    if (!cached || !Array.isArray(cached)) return [];
    return cached.map((fav: BookmarkItem) => ({
      label: fav.title,
      path: fav.to,
      icon: reconstructBookmarkIcon(fav, sidebarItems),
      groupKey: fav.fullPath && fav.fullPath.trim() ? fav.fullPath.split(' > ')[0].trim() : 'Bookmarks',
    }));
  });

  React.useEffect(() => {
    const loadBookmarks = async () => {
      try {
        const favs = await getBookmarks();
        setbookmarkEntries(
          (Array.isArray(favs) ? favs : []).map((fav: BookmarkItem) => ({
            label: fav.title,
            path: fav.to,
            icon: reconstructBookmarkIcon(fav, sidebarItems),
            groupKey: fav.fullPath && fav.fullPath.trim() ? fav.fullPath.split(' > ')[0].trim() : 'Bookmarks',
          }))
        );
      } catch (error) {
        console.error('Error loading bookmarks for top menu:', error);
      }
    };

    loadBookmarks();

    const handlebookmarksUpdated = () => {
      loadBookmarks();
    };

    window.addEventListener('bookmarksUpdated', handlebookmarksUpdated);
    return () => {
      window.removeEventListener('bookmarksUpdated', handlebookmarksUpdated);
    };
  }, [sidebarItems]);

  // Recursive function to find icon in nested menu items
  const findIconInMenuItems = (items: typeof sidebarItems, path: string): React.ReactNode | undefined => {
    for (const item of items) {
      // Check if this item matches
      if (item.path) {
        if (item.path === '/' && path === '/') {
          return item.icon;
        }
        if (item.path !== '/' && path.startsWith(item.path)) {
          return item.icon;
        }
      }
      
      // Check views for path match
      if (item.views) {
        const matchingView = item.views.find(view => {
          if (view.path === '/' && path === '/') return true;
          if (view.path !== '/' && path.startsWith(view.path)) return true;
          return false;
        });
        if (matchingView) {
          return item.icon;
        }
      }
      
      // Recursively check children
      if (item.children) {
        const childIcon = findIconInMenuItems(item.children, path);
        if (childIcon) {
          // Return parent icon if child doesn't have one, otherwise return child icon
          return childIcon || item.icon;
        }
        // If path matches a child path, return parent icon
        const hasMatchingChild = item.children.some(child => {
          if (child.path) {
            if (child.path === '/' && path === '/') return true;
            if (child.path !== '/' && path.startsWith(child.path)) return true;
          }
          // Check child views
          if (child.views) {
            return child.views.some(view => {
              if (view.path === '/' && path === '/') return true;
              if (view.path !== '/' && path.startsWith(view.path)) return true;
              return false;
            });
          }
          return false;
        });
        if (hasMatchingChild) {
          return item.icon;
        }
      }
    }
    return undefined;
  };

  // Get icon for current route
  const getIconForPath = (path: string): React.ReactNode | undefined => {
    // Custom icons for specific routes
    if (path === '/account-settings') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    }
    
    return findIconInMenuItems(sidebarItems, path);
  };
  
  // Map routes to titles
  const getPageInfo = (): { title?: string; subtitle?: string } => {
    const path = location.pathname;
    
    // Dashboard
    if (path === '/' || path === '/dashboard') {
      return { title: 'Dashboard' };
    }
    // Settings pages
    if (path === '/account-settings') {
      return { title: 'Account settings' };
    }

    if (path === '/admin/database-browser') {
      return { title: 'Database browser', subtitle: 'Inspect and edit PostgreSQL tables (admin only)' };
    }

    if (path.match(/^\/admin\/users\/\d+\/permissions$/)) {
      return { title: 'User permissions', subtitle: 'Module access for this user' };
    }

    if (path === '/dashboard' || path.match(/^\/vessel-modules\/\d+\/dashboard$/)) {
      return { title: 'Dashboard', subtitle: 'Vessel' };
    }

    if (path === '/admin/import-actions/impa-code') {
      return { title: 'IMPA Code', subtitle: 'Import store items from IMPA master CSV' };
    }

    if (path === '/import/invoice' || path.startsWith('/import/invoice/')) {
      return { title: 'Import Invoices' };
    }

    if (path === '/presets-management') {
      return {
        title: 'Presets Management',
      };
    }

    // Chat
    if (path === '/chat') {
      return { title: 'Chat' };
    }

    if (path.match(/^\/supplies\/supply-cases\/\d+\/case-view$/)) {
      return { title: 'Case activity', subtitle: 'Supply case pipeline' };
    }

    if (path.match(/^\/spares\/spare-cases\/\d+\/case-view$/)) {
      return { title: 'Case activity', subtitle: 'Spare case pipeline' };
    }

    if (path === '/supplies/port-calls' || path.startsWith('/supplies/port-calls/')) {
      return { title: 'Port calls', subtitle: 'Cases grouped by vessel, port, and ETA' };
    }

    if (path === '/spares/port-calls' || path.startsWith('/spares/port-calls/')) {
      return { title: 'Port calls', subtitle: 'Cases grouped by vessel, port, and ETA' };
    }

    // Vessel-scoped requisitions
    if (path.startsWith('/spare-requisitions') || path.match(/^\/vessel-modules\/\d+\/spare-requisitions(\/|$)/)) {
      if (path === '/spare-requisitions/new' || path.match(/^\/vessel-modules\/\d+\/spare-requisitions\/new$/)) {
        return {
          title: spareRequisitionsConfig.form?.new?.title || 'New spare requisition',
          subtitle: 'Vessel',
        };
      }
      if (
        path.match(/^\/spare-requisitions\/\d+\/edit$/) ||
        path.match(/^\/vessel-modules\/\d+\/spare-requisitions\/\d+\/edit$/)
      ) {
        return {
          title: spareRequisitionsConfig.form?.edit?.title || 'Edit spare requisition',
          subtitle: 'Vessel',
        };
      }
      return { title: spareRequisitionsConfig.module?.title || 'Spare Requisition', subtitle: 'Vessel' };
    }

    if (
      path.startsWith('/spare-pending-deliveries') ||
      path.match(/^\/vessel-modules\/\d+\/spare-pending-deliveries(\/|$)/)
    ) {
      if (
        path.match(/^\/spare-pending-deliveries\/\d+$/) ||
        path.match(/^\/vessel-modules\/\d+\/spare-pending-deliveries\/\d+$/)
      ) {
        return { title: 'Spare pending delivery', subtitle: 'Vessel' };
      }
      return { title: 'Spare pending deliveries', subtitle: 'Vessel' };
    }

    if (path.startsWith('/requisitions') || path.match(/^\/vessel-modules\/\d+\/requisitions(\/|$)/)) {
      if (path === '/requisitions/new' || path.match(/^\/vessel-modules\/\d+\/requisitions\/new$/)) {
        return {
          title: requisitionsConfig.form?.new?.title || 'New requisition',
          subtitle: 'Vessel',
        };
      }
      if (path.match(/^\/requisitions\/\d+\/edit$/) || path.match(/^\/vessel-modules\/\d+\/requisitions\/\d+\/edit$/)) {
        return {
          title: requisitionsConfig.form?.edit?.title || 'Edit requisition',
          subtitle: 'Vessel',
        };
      }
      if (path.match(/^\/requisitions\/\d+$/) || path.match(/^\/vessel-modules\/\d+\/requisitions\/\d+$/)) {
        return { title: requisitionsConfig.module?.title || 'Supply Requisition', subtitle: 'Vessel' };
      }
      return { title: requisitionsConfig.module?.title || 'Supply Requisition', subtitle: 'Vessel' };
    }

    if (path.startsWith('/pending-deliveries') || path.match(/^\/vessel-modules\/\d+\/pending-deliveries(\/|$)/)) {
      if (path.match(/^\/pending-deliveries\/\d+$/) || path.match(/^\/vessel-modules\/\d+\/pending-deliveries\/\d+$/)) {
        return { title: 'Pending delivery', subtitle: 'Vessel' };
      }
      return { title: 'Pending deliveries', subtitle: 'Vessel' };
    }

    // Find matching module config (sort by length descending to match most specific routes first)
    const sortedRoutes = Object.entries(routeToConfigMap).sort((a, b) => b[0].length - a[0].length);
    
    for (const [routePrefix, config] of sortedRoutes) {
      if (path.startsWith(routePrefix)) {
        // Check for new form
        if (path === `${routePrefix}/new` || (path.startsWith(routePrefix) && path.endsWith('/new'))) {
          const formTitle = config?.form?.new?.title;
          const moduleTitle = config?.module?.title || 'Item';
          return {
            title: formTitle || `New ${moduleTitle}`,
          };
        }
        // Check for edit form
        if (path.startsWith(routePrefix) && path.includes('/edit')) {
          const formTitle = config?.form?.edit?.title;
          const moduleTitle = config?.module?.title || 'Item';
          return {
            title: formTitle || `Edit ${moduleTitle}`,
          };
        }
        // Crew: dashboard vs full file view
        if (routePrefix === '/crew' && path.match(/^\/crew\/\d+\/file$/)) {
          const fileTitle = (config as { file?: { title?: string; subtitle?: string } })?.file?.title;
          const fileSubtitle = (config as { file?: { title?: string; subtitle?: string } })?.file?.subtitle;
          return { title: fileTitle ?? 'Crew file', subtitle: fileSubtitle };
        }
        if (routePrefix === '/crew' && path.match(/^\/crew\/\d+$/)) {
          return {
            title: 'Crew dashboard',
            subtitle: config?.detail?.subtitle ?? 'Overview',
          };
        }
        // Check for detail view (numeric ID after route prefix)
        if (path.match(new RegExp(`^${routePrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/\\d+$`))) {
          return {
            title: config?.module?.title || 'Details',
          };
        }
        // Default to module title for list view
        if (path === routePrefix || path.startsWith(`${routePrefix}/`)) {
          return {
            title: config?.module?.title || 'List',
          };
        }
      }
    }
    
    return {
      title: undefined,
    };
  };

  const { title, subtitle } = getPageInfo();
  const icon = getIconForPath(location.pathname);

  const { colors } = useTheme();
  const isDashboard = location.pathname === '/' || location.pathname === '/dashboard';
  const isFullHeightWorkspace =
    location.pathname === '/wrh/sheet' ||
    location.pathname === '/wrh/arrangements' ||
    location.pathname === '/wrh/compliance' ||
    location.pathname === '/wrh/reports' ||
    location.pathname === '/wrh/rules' ||
    location.pathname === '/vessel-operations';

  return (
    <div className={`flex h-screen ${colors.background}`}>
      <UserThemeSync />
      {showSidebar && (
        <Sidebar isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen} />
      )}
      <div
        className={cn(
          'flex-1 flex flex-col min-w-0 transition-all duration-300',
          showSidebar && 'overflow-hidden'
        )}
      >
        <Topbar
          title={title}
          subtitle={subtitle}
          icon={icon}
          isMobileMenuOpen={isMobileOpen}
          onToggleMobileMenu={() => setIsMobileOpen(!isMobileOpen)}
        />
        {showTopMenu && (
          <TopMenuNav items={topMenuSidebarItems} bookmarkEntries={bookmarkEntries} />
        )}
        <main
          className={cn(
            'flex-1 overflow-hidden min-w-0 flex flex-col',
            colors.background,
            isDashboard ? 'px-4 sm:px-6 pt-4 sm:pt-5 pb-4 sm:pb-5 w-full' : isCompact ? 'p-2 sm:p-4' : 'pt-1 px-4 pb-2'
          )}
        >
          <div className={cn(
            'flex flex-col min-w-0',
            (isDashboard || isFullHeightWorkspace) && 'h-full min-h-0 w-full',
            !isDashboard && !isFullHeightWorkspace && 'h-full min-h-0 overflow-y-auto',
            isFullHeightWorkspace && 'overflow-hidden',
          )}>
            <ErrorBoundary
              fallback={
                <div className="flex flex-col items-center justify-center flex-1 min-h-[320px] p-6 text-center">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">Something went wrong on this page.</p>
                  <Link to="/dashboard" className="text-indigo-600 dark:text-indigo-400 hover:underline">Back to Dashboard</Link>
                </div>
              }
            >
              <ModuleGuard>{children}</ModuleGuard>
            </ErrorBoundary>
          </div>
        </main>
        {appConfig.features.enableFooter && <Footer />}
      </div>
    </div>
  );
};

export default MainLayout;
